import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation, useQuery } from 'react-apollo'
import { useDispatch } from 'react-redux'
import { path, pathOr, trim } from 'ramda'
import classNames from 'classnames'

import {
  FORM_MESSAGES,
  FORM_VALUES,
  PHONE_MAX_LENGTH,
  PHONE_REG_EXP
} from '../../../../constants'
import {
  enableLoadingOverlay,
  endLoadingOverlay,
  updateAuthToken
} from '../../../../actions'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { REGEX } from '../../../../utils/regex'
import Form from '../../../forms/layout-form'
import Row from '../../../forms/layout-row'
import Col from '../../../forms/layout-column'
import Input from '../../../forms/input'
import Button from '../../../button'
import { CardWrapper, CardContent } from '../../../shared/settings/card'
import EditButton from '../../../shared/settings/edit-button'
import ACCOUNT_QUERY from '../../../../../graphql/queries/account.gql'
import ACCOUNT_MUTATION from '../../../../../graphql/mutations/account.gql'

import styles from './account-details-form.css'

const getDefaultValues = account => ({
  firstName: pathOr('', ['name'], account),
  lastName: pathOr('', ['surname'], account),
  email: pathOr('', ['email'], account),
  mobile: trim(pathOr('', ['phoneNumbers', 'mobile'], account))
})

export default function AccountDetailsForm() {
  const dispatch = useDispatch()
  const [isEditable, setIsEditable] = useState(false)
  const [formError, setFormError] = useState('')
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError
  } = useQuery(ACCOUNT_QUERY)
  const [updateAccount] = useMutation(ACCOUNT_MUTATION)
  const account = accountData?.account

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: {
      errors,
      touchedFields,
      isSubmitted,
      isSubmitting
    }
  } = useForm({
    defaultValues: getDefaultValues(account),
    mode: 'onChange'
  })

  useEffect(() => {
    if (account) {
      reset(getDefaultValues(account))
    }
  }, [account, reset])

  if (accountLoading || accountError) {
    return null
  }

  const onSubmit = async values => {
    setFormError('')

    const key = new Date().getTime()
    dispatch(enableLoadingOverlay(key, 'profilesEdit'))

    const payload = {
      name: values.firstName,
      surname: values.lastName,
      email: values.email,
      phoneNumbers: {
        mobile: trim(values.mobile)
      }
    }

    try {
      const { data: updateAccountData } = await updateAccount({
        variables: {
          input: payload
        }
      })
      const updatedAccount = pathOr({}, ['account'], updateAccountData)
      const token = path(['session', 'token'], updatedAccount)

      setValue('mobile', pathOr(null, ['phoneNumbers', 'mobile'], updatedAccount))
      setIsEditable(false)
      dispatch(updateAuthToken(token, true))
      dispatch(endLoadingOverlay(key, 'profilesEdit', { timeout: 2000 }))
    } catch (error) {
      setFormError(getGQLErrorMsg(error))
      dispatch(endLoadingOverlay(key, 'profilesEdit', { error }))
    }
  }

  const cancelEdit = (event) => {
    event.preventDefault()
    reset(getDefaultValues(account))
    setFormError('')
    setIsEditable(false)
  }

  const showWarning = (field, value) => (value || touchedFields[field] || isSubmitted) &&
    errors[field]?.message

  return (
    <CardWrapper>
      <CardContent className={styles.cardContent}>
        {!isEditable && (
          <div className={styles.editButton}>
            <EditButton
              data-e2e="account-details-edit-button"
              onClick={() => setIsEditable(true)}
            />
          </div>
        )}
        <Form
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
          formClassName={styles.formClass}
        >
          <Row className={styles.rowClass}>
            <Col className={styles.columnClass}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: FORM_MESSAGES.required }}
                render={({ field }) => (
                  <Input
                    data-e2e="account-details-first-name"
                    inputClass={styles.inputClass}
                    label="First name"
                    warning={showWarning(field.name, field.value)}
                    readonly={!isEditable}
                    {...field}
                  />
                )}
              />
            </Col>
            <Col className={styles.columnClass}>
              <Controller
                name="lastName"
                control={control}
                rules={{ required: FORM_MESSAGES.required }}
                render={({ field }) => (
                  <Input
                    data-e2e="account-details-last-name"
                    inputClass={styles.inputClass}
                    label="Last name"
                    warning={showWarning(field.name, field.value)}
                    readonly={!isEditable}
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
          <Row className={styles.rowClass}>
            <Col className={styles.columnClass}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: FORM_MESSAGES.required,
                  pattern: {
                    value: REGEX.email,
                    message: FORM_MESSAGES.email.valid
                  }
                }}
                render={({ field }) => (
                  <Input
                    data-e2e="account-details-email"
                    inputClass={classNames(styles.inputClass, styles.email)}
                    label="Email address"
                    warning={showWarning(field.name, field.value)}
                    readonly={!isEditable}
                    readonlyClassName={styles.email}
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
          <Row className={styles.rowClass}>
            <Col className={styles.columnClass}>
              <Controller
                name="mobile"
                control={control}
                rules={{
                  minLength: {
                    value: FORM_VALUES.mobile.min,
                    message: FORM_MESSAGES.mobile.min
                  },
                  maxLength: {
                    value: FORM_VALUES.mobile.max,
                    message: FORM_MESSAGES.mobile.max
                  },
                  pattern: {
                    value: PHONE_REG_EXP,
                    message: FORM_MESSAGES.mobile.match
                  }
                }}
                render={({ field }) => (
                  <Input
                    data-e2e="account-details-mobile-number"
                    inputClass={styles.inputClass}
                    label="Mobile number"
                    warning={showWarning(field.name, field.value)}
                    readonly={!isEditable}
                    readonlyClassName={styles.mobile}
                    inputmode="numeric"
                    maxLength={PHONE_MAX_LENGTH}
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
          {isEditable && (
            <Row>
              <Col>
                <Button
                  e2eIdentifier="account-details-save-button"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                  text="Save"
                  error={formError}
                  lightBGPrimary
                />
              </Col>
              <Col>
                <Button
                  e2eIdentifier="account-details-cancel-button"
                  className={styles.cancelButton}
                  disabled={isSubmitting}
                  text="Cancel"
                  secondary
                  onClick={cancelEdit}
                />
              </Col>
            </Row>
          )}
        </Form>
      </CardContent>
    </CardWrapper>
  )
}
